import type { TGetCommunicationTemplates } from "@api";
import type { CommunicationTemplateType } from "@bbj-types/communication/enum";
import type { IVariable, ObjectId } from "@bbj-types/utils";
import { UPLOAD_FILE_FIELD_BUTTON_HEIGHT } from "@inputs/file";
import AttachmentOutlinedIcon from "@mui/icons-material/AttachmentOutlined";
import { Stack, Typography } from "@mui/material";
import { Authorizer } from "@navigations/Authorizer";
import type { Dispatch, SetStateAction } from "react";
import type { Control, UseFormSetValue } from "react-hook-form";
import { filterEmailTemplateType } from "../logic";
import { isEmailEligibleCandidate } from "../logics/eligible";
import { type IRecipientsForm, Recipients } from "../recipients";
import type { INotifyByEmailModalForm, TCandidateData } from "../types";
import { Attachments } from "./attachments";
import { CcInput } from "./carbonCopy";
import { EmailContent } from "./content";
import { MenuTemplate } from "./menuTemplateMail";
import { ObjectInput } from "./object";

export interface IModalTemplate {
  _id: ObjectId;
  content: string;
  name: string;
  subject?: string;
  type: CommunicationTemplateType;
  organisation: string;
}

interface IEmailModalContentProps<T extends TGetCommunicationTemplates> {
  candidates: readonly TCandidateData[];
  control: Control<INotifyByEmailModalForm, void>;
  setValue: UseFormSetValue<INotifyByEmailModalForm>;
  getTemplates: () => T;
  variables: IVariable[];
  setAttachmentsLoading: Dispatch<SetStateAction<boolean>>;
}

export const EmailModalContent = <T extends TGetCommunicationTemplates>({
  candidates,
  control,
  getTemplates,
  setValue,
  variables,
  setAttachmentsLoading,
}: IEmailModalContentProps<T>) => {
  const templates = getTemplates();

  return (
    <>
      <MenuTemplate control={control} templates={templates.filter(filterEmailTemplateType)} setValue={setValue} />
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
        }}
      >
        <Typography sx={{ whiteSpace: "nowrap", minWidth: "7%" }} variant="body1">
          À * :
        </Typography>

        <Recipients
          label="Email candidat"
          getOptionDisabled={(option) => !isEmailEligibleCandidate(option)}
          control={control as unknown as Control<IRecipientsForm>}
          candidates={candidates}
        />
      </Stack>
      <CcInput control={control} />
      <ObjectInput control={control} variables={variables} />
      <Stack
        direction="row"
        sx={{
          alignItems: "flex-start",
        }}
      >
        <Stack
          sx={{
            width: "7%",
            minWidth: "fit-content",
            height: UPLOAD_FILE_FIELD_BUTTON_HEIGHT,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AttachmentOutlinedIcon color="secondary" />
        </Stack>
        <Authorizer
          requiredPermissions={["communication.email.attachments"]}
          Authorized={Attachments}
          AuthorizedProps={{ control, candidates, setAttachmentsLoading }}
        />
      </Stack>
      <EmailContent control={control} variables={variables} />
    </>
  );
};
